import { ScrollText } from "lucide-react";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { EnumSelect } from "@/components/shared/enum-select";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { SearchInput } from "@/components/shared/search-input";
import { Badge } from "@/components/ui/badge";
import { type AuditLogEntry, useAuditLog } from "@/hooks/use-audit";
import { DEFAULT_LIMIT, useDataGrid } from "@/hooks/use-data-grid";
// `diffChanges` lives in the dependency-free `@/lib/diff` (not `@/lib/audit`) so
// this client component doesn't pull the server-only audit module's
// drizzle/postgres deps into the browser bundle.
import { diffChanges } from "@/lib/diff";
import { AUDIT_ACTION_OPTIONS } from "@/lib/enums";
import { formatDateTime } from "@/lib/format";

function summarizeChange(entry: AuditLogEntry): string {
  if (!entry.before && entry.after) return "created";
  if (entry.before && !entry.after) return "deleted";
  if (entry.before && entry.after) {
    const keys = diffChanges(entry.before, entry.after);
    return keys.length > 0 ? keys.join(", ") : "—";
  }
  const email = entry.metadata?.email;
  if (typeof email === "string") return email;
  return "—";
}

export function AuditLogTab() {
  const grid = useDataGrid({ defaultSort: "createdAt" });
  const { data, isLoading, isError, error, refetch } = useAuditLog(grid.query, grid.pageRequest);

  const rows = data?.data ?? [];

  const toolbar = (
    <div className="flex items-center gap-4">
      <SearchInput
        value={grid.searchInput}
        onValueChange={grid.setSearch}
        placeholder="Search by actor or entity..."
        className="flex-1 max-w-sm"
      />
      <EnumSelect
        value={grid.status}
        onValueChange={grid.setStatus}
        options={AUDIT_ACTION_OPTIONS}
        allOption={{ label: "All actions" }}
        placeholder="All actions"
        className="w-[220px]"
        aria-label="Filter by action"
      />
    </div>
  );

  if (isError) {
    return (
      <div className="space-y-4">
        {toolbar}
        <ErrorState
          message={error?.message ?? "Failed to load audit log"}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {toolbar}

      <DataTable
        headers={[
          { label: "When", sortKey: "createdAt" },
          { label: "Actor" },
          { label: "Action", sortKey: "action" },
          { label: "Entity", className: "hidden md:table-cell", sortKey: "entityType" },
          { label: "Changes", className: "hidden lg:table-cell" },
        ]}
        sort={grid.sort}
        sortDir={grid.sortDir}
        onSort={grid.toggleSort}
        loading={isLoading}
        empty={
          <EmptyState
            icon={ScrollText}
            title="No audit entries"
            description="Activity will appear here as users create, change, and delete records."
          />
        }
      >
        {rows.map((entry) => (
          <tr key={entry.id} className="border-b border-border last:border-0 hover:bg-muted/30">
            <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
              {formatDateTime(entry.createdAt)}
            </td>
            <td className="px-4 py-3 font-medium max-w-[200px] truncate">
              {entry.actorEmail ?? entry.actorId ?? "system"}
            </td>
            <td className="px-4 py-3">
              <Badge variant="outline" className="font-mono text-xs">
                {entry.action}
              </Badge>
            </td>
            <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">
              {entry.entityType}
              {entry.entityId ? (
                <span className="text-muted-foreground/60"> · {entry.entityId.slice(0, 8)}</span>
              ) : null}
            </td>
            <td className="hidden lg:table-cell px-4 py-3 text-muted-foreground max-w-xs truncate">
              {summarizeChange(entry)}
            </td>
          </tr>
        ))}
      </DataTable>

      <CursorPagination
        total={data?.total ?? 0}
        page={grid.page}
        limit={DEFAULT_LIMIT}
        hasNext={data?.hasNext ?? false}
        hasPrev={data?.hasPrev ?? false}
        onNext={() => grid.goNext(data?.nextCursor ?? null)}
        onPrev={() => grid.goPrev(data?.prevCursor ?? null)}
        loading={isLoading}
        itemLabel="entries"
      />
    </div>
  );
}
